import { useEffect, useState } from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import AddButton from 'modules/shift_management/components/ShiftManagement/AddButton'
import AddShiftSidepanel from 'modules/shift_management/components/ShiftManagement/AddShiftSidepanel'
import { countCellsSelected } from 'modules/shift_management/hooks/useCountCellsSelected'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import { trackAddShiftStartEvent } from 'modules/shift_management/lib/trackEvents'
import { type ShiftsRow } from 'modules/shift_management/lib/types'
import type DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'

import Content from './Content'

interface Props {
  date: moment.Moment
  shiftsRow: ShiftsRow
  visibleDefaultShifts: DefaultShifts
  isDefaultShiftsLoading?: boolean
  forceOpen?: boolean
  forceAnchorRect?: DOMRect
  onCloseForced?: () => void
}

const QuickActionsPopover = ({
  date,
  shiftsRow,
  visibleDefaultShifts,
  isDefaultShiftsLoading = false,
  forceOpen = false,
  forceAnchorRect,
  onCloseForced,
}: Props) => {
  const [open, toggle] = useToggle(false)
  const [openAddShiftSidePanel, setOpenAddShiftSidePanel] = useState<boolean>(false)
  const [{ view }] = useSchedule()
  const { daysByRow, showEmptyShiftCell, shifts, restDays } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()

  const applySelectionAndTracking = () => {
    const selectedEmployees = Object.keys(daysByRow).length
    const impactedEmployees = selectedEmployees || 1
    const cellCount = countCellsSelected({ daysByRow, shifts, restDays, view })
    const impactedShifts = cellCount || 1

    dispatchSelection({
      type: 'SHOW_EMPTY_SHIFT_CELL',
    })
    // Forcing to enable the day if it's not selected.
    dispatchSelection({
      type: 'TOGGLE_DAY',
      day: date,
      shiftsRow,
      view,
      forcedValue: true,
    })
    trackAddShiftStartEvent({
      view,
      impactedEmployees,
      cellCount: impactedShifts,
      shiftType: 'savedCustom',
      from: 'contextual_button',
      quickActions: true,
    })
  }

  const onOpenPopover = () => {
    toggle(true)()
    applySelectionAndTracking()
  }

  const onClosePopover = () => {
    dispatchSelection({
      type: 'CLEAR_SELECTION',
    })
    toggle(false)()
    onCloseForced?.()
  }

  const onOpenSidePanel = () => {
    dispatchSelection({
      type: 'HIDE_EMPTY_SHIFT_CELL',
    })
    toggle(false)()
    setOpenAddShiftSidePanel(true)
  }

  const onCloseSidePanel = () => {
    setOpenAddShiftSidePanel(false)
    onCloseForced?.()
  }

  const onInteractOutside = () => {
    dispatchSelection({
      type: 'HIDE_EMPTY_SHIFT_CELL',
    })
    toggle(false)()
    onCloseForced?.()
  }

  useEffect(() => {
    if (!forceOpen) return
    if (view.isByAreas) {
      setOpenAddShiftSidePanel(true)
      return
    }
    if (isDefaultShiftsLoading || visibleDefaultShifts?.active?.length) return
    setOpenAddShiftSidePanel(true)
  }, [forceOpen, isDefaultShiftsLoading, view.isByAreas, visibleDefaultShifts?.active?.length])

  if (forceOpen && view.isByAreas) {
    return (
      // Stop propagation to avoid unselecting the cell on the background.
      <Box onClick={(ev) => ev.stopPropagation()}>
        {openAddShiftSidePanel && <AddShiftSidepanel onClose={onCloseSidePanel} date={date} />}
      </Box>
    )
  }

  if (!isDefaultShiftsLoading && !visibleDefaultShifts?.active?.length) {
    return (
      // Stop propagation to avoid unselecting the cell on the background.
      <Box onClick={(ev) => ev.stopPropagation()}>
        {!forceOpen && <AddButton onClick={() => setOpenAddShiftSidePanel(true)} />}
        {openAddShiftSidePanel && (
          <AddShiftSidepanel onClose={onCloseSidePanel} date={date} noSavedShifts />
        )}
      </Box>
    )
  }

  return (
    // Stop propagation to avoid unselecting the cell on the background.
    <Box onClick={(ev) => ev.stopPropagation()}>
      <PopoverMenu type="dialog" open={!openAddShiftSidePanel && (forceOpen || open)}>
        {forceOpen && forceAnchorRect ? (
          <PopoverMenu.Trigger asChild>
            <Box
              style={{
                position: 'fixed',
                left: `${forceAnchorRect.left}px`,
                top: `${forceAnchorRect.top}px`,
                width: `${Math.max(1, forceAnchorRect.width)}px`,
                height: `${Math.max(1, forceAnchorRect.height)}px`,
                pointerEvents: 'none',
              }}
            />
          </PopoverMenu.Trigger>
        ) : (
          <PopoverMenu.Trigger>
            {/* Hide contextual button when is assigning saved shifts from popover */}
            {!showEmptyShiftCell && <AddButton onClick={onOpenPopover} />}
          </PopoverMenu.Trigger>
        )}

        <PopoverMenu.Content
          side="right"
          align="center"
          width="s360"
          onInteractOutside={onInteractOutside}
        >
          <Box paddingY="s12" paddingX="s24" gap="s16" maxHeight="s400">
            <Content savedShifts={visibleDefaultShifts?.active} onClose={onClosePopover} />
            <Box
              flexDirection="row"
              justifyContent="flexEnd"
              onClick={(ev) => ev.stopPropagation()}
            >
              <Button onClick={onOpenSidePanel} type="secondary" iconName="add">
                {i18n.t('shift_management.quick_actions.advanced_options')}
              </Button>
            </Box>
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
      {openAddShiftSidePanel && (
        <Box onClick={(ev) => ev.stopPropagation()}>
          <AddShiftSidepanel onClose={onCloseSidePanel} date={date} advancedOptions />
        </Box>
      )}
    </Box>
  )
}

export default observer(QuickActionsPopover)
